import * as React from 'react';
import Modal, { ModalSize } from '../misc/components/modal';
import { Direction, useQuery } from './Query';
import Select from 'react-select';
import { FilterHeader } from './QueryTable';

export interface Option {
    label: any;
    value: any;
}

interface AnalysisSetsOverreadStatusHeaderProps {
    title?: string;
    overreadStatuses: Option[];
    overreadChallengeStatuses: Option[];
    challengeField?: string;
    sortField?: string;
    automationId?: string;
}

export const AnalysisSetsOverreadStatusHeader = ({ title, overreadStatuses, overreadChallengeStatuses, sortField = null, automationId = null }: AnalysisSetsOverreadStatusHeaderProps) => {
    const [display, setDisplay] = React.useState(false);
    const { filters, ordering, applyOrdering } = useQuery();
    const [ordered, setOrdered] = React.useState<Direction>();
    const [filtered, setFiltered] = React.useState(false);

    React.useEffect(() => {
        const filtered = filters.some(f => (f.field === 'status' && f.condition === 'eq' || f.field === 'sessions/overreadChallengeStatus' && f.condition === 'eq'));
        setFiltered(filtered);
    }, [filters]);

    React.useEffect(() => {

        const ordered = ordering.find(o => o.field === sortField);
        setOrdered(ordered
            ? ordered.condition
            : null);
    }, [ordering]);

    const handleSort = () => {
        let direction: Direction = 'asc';

        if (ordered === 'asc')
            direction = 'desc';

        applyOrdering([{ field: sortField, condition: direction }]);
    }

    const handleFilter = () => {
        setDisplay(true);
    }

    const modal = display
        ? (
            <AnalysisSetsOverreadStatusFilterModal
                title={title}
                overreadStatuses={overreadStatuses}
                overreadChallengeStatuses={overreadChallengeStatuses}
                onClose={() => setDisplay(false)} />
        )
        : null;

    return (
        <>
            <FilterHeader automationId={automationId} filtered={filtered} sorted={ordered} onFilter={handleFilter} onSort={handleSort}>{title}</FilterHeader>
            {modal}
        </>
    );
}

interface AnalysisSetsOverreadStatusFilterModalProps {
    title?: string;
    overreadStatuses: any[];
    overreadChallengeStatuses: any[];
    onClose: () => void;
}

export const AnalysisSetsOverreadStatusFilterModal = ({ title, overreadStatuses, overreadChallengeStatuses, onClose }: AnalysisSetsOverreadStatusFilterModalProps) => {
    const [statusValues, setStatusValues] = React.useState<any[]>([]);
    const [challengeValues, setChallengeValues] = React.useState<any[]>([]);

    const { filters, applyFilters, paging, applyPaging } = useQuery();

    React.useEffect(() => {
        const currentStatusValues = filters
            .filter(f => f.field === "status" && f.condition === 'eq')
            .flatMap(f => f.values);

        const currentChallengeValues = filters
            .filter(f => f.field === "session/overreadChallengeStatus" && f.condition === 'eq')
            .flatMap(f => f.values);

        setStatusValues(currentStatusValues);
        setChallengeValues(currentChallengeValues);
    }, [filters]);

    const handleApply = () => {
        let newFilters = filters.filter(f => f.field !== "status" && f.field !== "session/overreadChallengeStatus");

        statusValues && statusValues.length > 0 && (
            newFilters = [...newFilters,
            {
                field: "status",
                condition: 'eq',
                values: statusValues,
            }
            ]);

        challengeValues && challengeValues.length > 0 && (
            newFilters = [...newFilters, {
                field: "session/overreadChallengeStatus",
                condition: 'eq',
                values: challengeValues,
            }]);

        applyPaging({ ...paging, page: 1 });
        applyFilters(newFilters);
        onClose();
    }

    const handleClear = () => {
        applyPaging({ ...paging, page: 1 });

        applyFilters(filters.filter(
            f =>
                (
                    f.field !== "status"
                )
                &&
                (
                    f.field !== "session/overreadChallengeStatus"
                )
        ));

        onClose();
    }

    return (
        <Modal header={`${title} Filter`} size={ModalSize.Normal} show={true} closeModalHandler={onClose}>
            <div className="modal-body">
                <label>Status</label>
                <Select
                    id="overread-status-match"
                    name="match1"
                    isMulti={true}
                    classNamePrefix="multiSelect"
                    options={overreadStatuses}
                    autoFocus={true}
                    value={statusValues.map(v => overreadStatuses.find(o => o.value === v))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    onChange={(o: any[]) => setStatusValues(o.map(v => v.value))}
                />
            </div>
            <div className="modal-body">
                <label>Challenge Status</label>
                <Select
                    name="match2"
                    id="overread-challenge-status-match"
                    isMulti={true}
                    classNamePrefix="multiSelect"
                    options={overreadChallengeStatuses}
                    autoFocus={true}
                    value={challengeValues.map(v => overreadChallengeStatuses.find(o => o.value === v))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    onChange={(o: any[]) => setChallengeValues(o.map(v => v.value))}
                />
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleApply} data-automationid="submit-filter">OK</button>
                <button className="btn btn-danger" onClick={handleClear} data-automationid="clear-filter">Clear</button>
            </div>
        </Modal>
    );
}
